/**
 * The tab strip every workflow-ish page has been drawing by hand (DOP-478).
 *
 * The exact same markup — wrapper, `-mb-px border-b-2`, the teal active pair,
 * `aria-current="page"` — was copied into the KPI workflow, the monitoring
 * template page and the monitoring run page. Three copies of one control is
 * three places for it to drift, and the unified workflow shell needs a fourth.
 * So it becomes a component, with the two things the copies actually differed
 * on as props: a right-aligned slot (the run page's Analyst toggle) and an
 * optional count beside a label.
 */

import React from "react";

export interface PageTab {
	id: string;
	label: string;
	/** Rendered as a small badge after the label. Omit for no badge. */
	count?: number;
	/**
	 * `attention` draws the count in the warning palette — a number of things
	 * waiting on the user ("Needs review"), not a plain tally.
	 */
	countTone?: "neutral" | "attention";
	/** A leading icon. Decorative: the label still names the tab. */
	icon?: React.ReactNode;
	/** A short marker after the label, e.g. "New" for a tab that just arrived. */
	badge?: string;
	/** Greyed out and not selectable — the tab has nothing to show yet. */
	disabled?: boolean;
}

export interface PageTabsProps {
	tabs: readonly PageTab[];
	activeId: string;
	onSelect: (id: string) => void;
	/** Names the strip for screen readers — "KPI scope", "Run sections". */
	ariaLabel: string;
	/** Pushed to the right edge of the strip, after the last tab. */
	actions?: React.ReactNode;
}

const PageTabs: React.FC<PageTabsProps> = ({
	tabs,
	activeId,
	onSelect,
	ariaLabel,
	actions,
}) => (
	<nav
		className="flex items-center gap-1 border-b border-gray-200 px-6"
		aria-label={ariaLabel}
	>
		{tabs.map((tab) => {
			const isActive = tab.id === activeId;
			return (
				<button
					key={tab.id}
					type="button"
					onClick={() => onSelect(tab.id)}
					disabled={tab.disabled}
					aria-current={isActive ? "page" : undefined}
					data-tab-id={tab.id}
					className={`-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium ${
						isActive
							? "border-[#004C4B] font-semibold text-[#004C4B]"
							: tab.disabled
								? "cursor-not-allowed border-transparent text-gray-300"
								: "border-transparent text-text-muted hover:text-text-base"
					}`}
				>
					{tab.icon}
					{tab.label}
					{typeof tab.count === "number" && (
						<span
							className={`rounded-full px-1.5 py-0.5 text-xs ${
								tab.countTone === "attention" && tab.count > 0
									? "bg-[#fffaeb] font-semibold text-[#b54708]"
									: "bg-gray-100 text-text-muted"
							}`}
						>
							{tab.count}
						</span>
					)}
					{tab.badge && (
						<span className="rounded-full bg-[#004C4B] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
							{tab.badge}
						</span>
					)}
				</button>
			);
		})}
		{actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
	</nav>
);

export default PageTabs;
